import { addMessages, init, register } from "svelte-i18n"

import { deepMerge } from "@/lib/deep-merge"
import en from "@/locales/en.json"

type LocaleModule = { default: Record<string, unknown> }

const codeFromPath = (path: string) => path.match(/([^/]+)\.json$/)?.[1]

// One lazy loader per base dictionary in locales/*.json. The glob's keys are
// known synchronously (nothing is fetched here), so the language list needs
// no manifest — adding a language is adding its JSON file.
const baseLoaders: Record<string, () => Promise<LocaleModule>> = {}
for (const [path, loader] of Object.entries(
  import.meta.glob<LocaleModule>("../locales/*.json"),
)) {
  const code = codeFromPath(path)
  if (code && code !== "en") baseLoaders[code] = loader
}

// Region overlays (locales/regions/<lang>-<CC>.json): a few region-specific
// marketing keys (homepage hero copy) layered over the base language when
// the server geo-resolves the visitor's country to an overlay
// (ResolvesRegion — URLs never carry a country). An overlay applies exactly
// where an overlay file exists — the glob doubles as the manifest. Lazy
// like the bases.
const regionLoaders: Record<string, () => Promise<LocaleModule>> = {}
for (const [path, loader] of Object.entries(
  import.meta.glob<LocaleModule>("../locales/regions/*.json"),
)) {
  const code = codeFromPath(path)
  if (code) regionLoaders[code] = loader
}

/** Canonical region overlay codes with a merged dictionary ("de-AT",
 *  "en-US", …). Mirrors Locales::REGIONS on the Ruby side. */
export const REGION_CODES = Object.keys(regionLoaders)

/** The public code for a language + ISO country pair: the region code when
 *  that overlay exists (de + CH -> "de-CH"), else the bare language. The
 *  language switcher + suggestion banner use this for the in-tab
 *  dictionary flip; only the base language is ever persisted server-side. */
export function regionLocale(lang: string, country?: string | null): string {
  const code = `${lang}-${country}`
  return country && regionLoaders[code] ? code : lang
}

/** Raw base dictionary for a supported language, en included. Same lazy
 *  loaders as the svelte-i18n registration below (the module cache dedupes
 *  the fetch), so the language-suggestion banner can render its strings in
 *  the language it OFFERS without flipping the page's active $locale. */
export async function baseDictionary(
  lang: string,
): Promise<Record<string, unknown> | null> {
  if (lang === "en") return en
  const loader = baseLoaders[lang]
  return loader ? (await loader()).default : null
}

// Client-side i18n bootstrap. English is bundled synchronously as the
// fallback so `$_` can never render raw message ids; every other locale
// registers an async loader and only the active one is fetched (statically
// importing all of them would ship every ~50KB dictionary in the critical
// vendor chunk, parsed on every page load). The SSR entry loads all
// locales eagerly instead — see ssr/ssr.ts.
export function setupClientI18n(initialLocale: string) {
  addMessages("en", en)
  for (const [code, loader] of Object.entries(baseLoaders)) register(code, loader)

  // Region codes (e.g. "de-CH") register a merged dictionary: their base
  // language's full JSON (same loader as above, so the browser's module
  // cache dedupes the fetch) deep-merged with the overlay's few overridden
  // keys. svelte-i18n has no hierarchical de-CH -> de -> en fallback of its
  // own, so the merge must produce one complete dictionary before it's
  // registered.
  for (const [code, loader] of Object.entries(regionLoaders)) {
    const base = code.split("-")[0]
    register(code, async () => {
      const [baseDict, override] = await Promise.all([
        base === "en" ? Promise.resolve({ default: en } as LocaleModule) : baseLoaders[base](),
        loader(),
      ])
      return deepMerge(baseDict.default, override.default)
    })
  }

  init({ fallbackLocale: "en", initialLocale })
}

// The shared `locale` prop of the Inertia page embedded in the initial HTML
// (a base language, or a geo-resolved region code), read before the app
// boots so hydration runs with the same locale the server rendered.
export function initialLocaleFromPage(): string {
  try {
    const el = document.querySelector(
      'script[type="application/json"][data-page]',
    )
    if (!el?.textContent) return "en"
    const parsed = JSON.parse(el.textContent) as {
      props?: { locale?: string }
    }
    return parsed.props?.locale ?? "en"
  } catch {
    return "en"
  }
}
